import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Spin,
  Modal,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import {
  createHostRentBill,
  updateHostRentBill,
  getHostRentBillDetail,
  getHostRentContractList,
} from "../../api/host";
import { toBjNoon, fromBj, fmtBj, today, r2 } from "../../utils/bjDate";
import {
  BILL_TYPE_MAP,
  RENT_CYCLE_MAP,
  WATER_PRICE,
  ELECTRICITY_PRICE,
  BILL_TYPE_COLOR,
} from "./rentBillConstants";
import HostSelect from "./HostSelect";

const BILL_TYPE_OPTIONS = [
  "rent",
  "deposit",
  "waterElectricity",
  "propertyFee",
  "other",
].map((v) => ({
  value: v,
  label: BILL_TYPE_MAP[v],
}));
const STATUS_META = {
  unpaid: { label: "未支付", color: "#dc2626" },
  partiallyPaid: { label: "部分支付", color: "#d97706" },
  paid: { label: "已支付", color: "#16a34a" },
  closed: { label: "已关闭", color: "#94a3b8" },
  cancelled: { label: "已取消", color: "#94a3b8" },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({
  value,
  label: <span style={{ color: m.color }}>{m.label}</span>,
}));

// 输入焦点顺序：账单类型 -> 账单名称(其他类型才有) -> 开始日期 ->
//   水电：水表起 -> 电表起 -> 账单日期（结束日期、金额、止读数在抄表时同步，不在顺序里）
//   其他：结束日期 -> 应付金额 -> 账单日期
const ORDER_WE = [
  "billType",
  "billNames",
  "startDate",
  "waterMeterStart",
  "electricityMeterStart",
  "dueDate",
];
const ORDER_DEFAULT = [
  "billType",
  "billNames",
  "startDate",
  "endDate",
  "amount",
  "dueDate",
];
const METER_KEYS = [
  "waterMeterStart",
  "waterMeterEnd",
  "electricityMeterStart",
  "electricityMeterEnd",
];
const FMT = "YYYY/MM/DD";

const css = `
.rbd{max-width:560px;margin:0 auto;}
.rbd-row{display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;}
.rbd-label{flex:0 0 4.4em;width:4.4em;line-height:32px;font-size:13px;color:#64748b;white-space:nowrap;overflow:hidden;
  transition:flex-basis .2s,width .2s,opacity .2s;}
/* 聚焦时隐藏标题，把宽度让给输入框 */
.rbd-row:focus-within>.rbd-label{flex-basis:0;width:0;opacity:0;}
.rbd-cell{min-width:0;}
.rbd-chip{flex:0 0 auto;height:32px;padding:0 12px;border-radius:8px;border:1px solid #d9d9d9;background:#fff;color:#64748b;
  font:inherit;font-size:13px;display:inline-flex;align-items:center;gap:4px;cursor:pointer;-webkit-tap-highlight-color:transparent;}
.rbd-chip.on{border-color:#2563eb;background:#eff6ff;color:#1d4ed8;}
.rbd-v{display:flex;flex-wrap:wrap;gap:8px;padding:6px 0;font-size:14px;line-height:1.6;color:#0f172a;border-bottom:1px solid rgba(148,163,184,.18);}
.rbd-v:last-child{border-bottom:0;}
.rbd-vr{margin-left:auto;display:flex;gap:8px;white-space:nowrap;}
.rbd-vl{flex:0 0 3em;color:#94a3b8;font-size:13px;}
.rbd-vv{flex:1;min-width:0;word-break:break-word;white-space:pre-wrap;}
.rbd-tag{display:inline-block;padding:0 8px;border-radius:8px;font-size:12px;line-height:20px;background:#f1f5f9;color:#475569;margin-left:6px;}.rbd-head{display:flex;align-items:center;gap:8px;padding:2px 0 8px;border-bottom:1px solid rgba(148,163,184,.18);}
.rbd-type{display:inline-block;padding:0 10px;border-radius:8px;background:var(--tc);color:#fff;font-size:13px;font-weight:600;line-height:26px;}
.rbd-room{font-size:17px;font-weight:600;color:#0f172a;}
.rbd-foot{display:flex;justify-content:flex-end;gap:8px;max-width:560px;margin:0 auto;}
`;

// contractName 本身就是「房号-付款方式-yyyymmdd」，有就直接用；没有才按同样格式拼
const contractLabel = (c) =>
  c?.contractName ||
  `${c?.host?.roomNumber || "合约"}-${RENT_CYCLE_MAP[c?.rentPaymentCycle] || ""}-${fmtBj(c?.contractSignDate, "YYYYMMDD")}`;

// 表单日期 -> 入库值（北京时间 13:00）；空值原样保留
const dateFields = (v) => ({
  startDate: v.startDate ? toBjNoon(v.startDate) : v.startDate,
  endDate: v.endDate ? toBjNoon(v.endDate) : v.endDate,
  dueDate: v.dueDate ? toBjNoon(v.dueDate) : v.dueDate,
});

const n2 = (v) =>
  v == null
    ? "-"
    : Number(v).toLocaleString("zh-CN", { maximumFractionDigits: 2 });

// 开关按钮（配合 Form.Item 使用）
function ToggleChip({ value, onChange, children }) {
  return (
    <button
      type="button"
      className={`rbd-chip${value ? " on" : ""}`}
      aria-pressed={!!value}
      onClick={() => onChange?.(!value)}
    >
      {value && <CheckOutlined />}
      {children}
    </button>
  );
}

const Row = ({ label, children }) => (
  <div className="rbd-row">
    {label && <label className="rbd-label">{label}</label>}
    {children}
  </div>
);
const Cell = ({ flex = 1, children }) => (
  <div className="rbd-cell" style={{ flex }}>
    {children}
  </div>
);
const Item = (props) => <Form.Item style={{ marginBottom: 0 }} {...props} />;
const View = ({ label, children }) => (
  <div className="rbd-v">
    <span className="rbd-vl">{label}</span>
    <span className="rbd-vv">{children}</span>
  </div>
);

/**
 * 账单抽屉：查看(view) / 编辑(edit) / 新增(create)
 * 打开已有账单先只读；编辑时锁定房间、账单类型、已付金额（已付金额只能通过结算产生），合约可改但不联动房间
 */
export default function RentBillDrawer({
  open,
  id,
  adviceContract,
  billChange,
  close,
}) {
  const [form] = Form.useForm();
  const [mode, setMode] = useState("view");
  const [data, setData] = useState(null); // 只读视图用的账单数据
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contractOptions, setContractOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const billType = Form.useWatch("billType", form);
  const isOther = billType === "other";

  const rootRef = useRef(null);

  // 跳到顺序中的下一个存在且可用的字段（被隐藏或禁用的会跳过）
  const goNext = (from) => {
    const root = rootRef.current;
    if (!root) return;
    const order =
      form.getFieldValue("billType") === "waterElectricity"
        ? ORDER_WE
        : ORDER_DEFAULT;
    for (let i = order.indexOf(from) + 1; i < order.length; i++) {
      const el = root.querySelector(
        `[data-f="${order[i]}"] input:not([disabled]), [data-f="${order[i]}"] textarea`,
      );
      if (el) {
        setTimeout(() => el.focus(), 0); // 等选择框收起后再聚焦
        return;
      }
    }
  };
  // 回车 / 输入法"下一项"：按顺序跳转（输入法选字中的回车不算）
  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.nativeEvent?.isComposing || e.keyCode === 229)
      return;
    if (e.target.tagName === "TEXTAREA") return;
    const holder = e.target.closest?.("[data-f]");
    if (holder) {
      e.preventDefault();
      goNext(holder.dataset.f);
    }
  };

  // 账单日：水电账单手动定；日租合约 = 开始日期；其他 = 开始日期 - 预付天数
  const contractOf = (cid) =>
    adviceContract?._id === cid
      ? adviceContract
      : contractOptions.find((o) => o.value === cid)?.raw;
  const autoDue = (all) => {
    if (
      !all.startDate ||
      ["waterElectricity", "deposit"].includes(all.billType)
    )
      return;
    const c = contractOf(all.contract);
    if (!c) return;
    if (c.rentPaymentCycle === "daily")
      return form.setFieldsValue({ dueDate: all.startDate });
    if (c.prePaidDays == null) return; // 拿不到预付天数就不自动算，免得覆盖用户填的
    form.setFieldsValue({
      dueDate: all.startDate.subtract(Number(c.prePaidDays) || 0, "day"),
    });
  };
  // 合约下拉：进入编辑/新增时才拉，并保证当前合约一定在选项里
  const loadContractOptions = async (currentOption) => {
    setOptionsLoading(true);
    try {
      const res = await getHostRentContractList({
        status: ["active", "pending"],
      });
      const list = [...(res.data || [])];
      if (
        adviceContract?._id &&
        !list.some((x) => x._id === adviceContract._id)
      )
        list.push(adviceContract);
      const options = list.map((c) => ({
        value: c._id,
        label: contractLabel(c),
        host: c.host,
      }));
      if (
        currentOption &&
        !options.some((o) => o.value === currentOption.value)
      )
        options.push(currentOption);
      setContractOptions(options);
    } catch (e) {
      message.error("加载合约列表失败");
    } finally {
      setOptionsLoading(false);
    }
  };

  const toFormValues = (d) => ({ ...d, host: d.hostId });

  const loadDetail = async () => {
    setLoading(true);
    try {
      const b = (await getHostRentBillDetail(id)).data;
      const c = b.contract || {};
      const d = {
        ...b,
        contract: c._id,
        contractText:
          c._id && c._id === adviceContract?._id
            ? contractLabel(adviceContract)
            : contractLabel({ ...c, host: c.host || b.host }),
        hostId: b.host?._id,
        roomNumber: b.host?.roomNumber,
        startDate: fromBj(b.startDate),
        endDate: fromBj(b.endDate),
        dueDate: fromBj(b.dueDate),
      };
      setData(d);
      return d;
    } catch (e) {
      message.error(e?.response?.data?.message || "加载账单失败");
      close();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setData(null);
    if (id) {
      setMode("view");
      loadDetail();
    } else {
      setMode("create");
      form.setFieldsValue({
        billType: "other",
        oncePay: true,
        contract: adviceContract?._id,
        host: adviceContract?.host?._id,
        startDate: today(),
        endDate: today(),
        dueDate: today(),
        paidAmount: 0,
        status: "unpaid",
      });
      loadContractOptions();
      autoDue(form.getFieldsValue(true));
      const t = setTimeout(
        () =>
          rootRef.current?.querySelector('[data-f="billType"] input')?.focus(),
        400,
      ); // 等抽屉动画结束
      return () => clearTimeout(t);
    }
  }, [open, id]);

  const startEdit = () => {
    form.setFieldsValue(toFormValues(data));
    setMode("edit");
    loadContractOptions({
      value: data.contract,
      label: data.contractText,
      host: { _id: data.hostId, roomNumber: data.roomNumber },
    });
  };

  const isDirty = () => mode !== "view" && form.isFieldsTouched();
  const confirmDiscard = (onOk) =>
    isDirty()
      ? Modal.confirm({
          title: "放弃未保存的修改？",
          okText: "放弃",
          cancelText: "继续编辑",
          onOk,
        })
      : onOk();
  const requestClose = () => confirmDiscard(close);
  const cancelEdit = () =>
    confirmDiscard(() => {
      if (mode === "create") close();
      else setMode("view");
    });

  // 根据已付/应付金额推导状态（已关闭/已取消不联动）
  const syncStatus = (all) => {
    if (!["unpaid", "partiallyPaid", "paid"].includes(all.status)) return;
    const paid = Number(all.paidAmount) || 0;
    const amt = Number(all.amount) || 0;
    let next = "unpaid";
    if (amt == 0) {
      //兼容已付金额大于应付金额的情况
      next = paid > 0 ? "partiallyPaid" : "unpaid";
    } else {
      next = paid <= 0 ? "unpaid" : paid >= amt ? "paid" : "partiallyPaid";
    }
    if (next !== all.status) form.setFieldsValue({ status: next });
  };

  // 只响应用户的修改（setFieldsValue 不会触发），所以不会在打开时覆盖已存金额
  const onValuesChange = (changed, all) => {
    if (changed.contract && mode === "create") {
      const host = contractOptions.find(
        (o) => o.value === changed.contract,
      )?.host;
      form.setFieldsValue({ host: host?._id });
    }
    if ("startDate" in changed || changed.billType || changed.contract)
      autoDue(all);
    // 新增时按类型给"一次性"一个合理的默认值：押金、其他=一次性；租金、水电、物业=周期性
    if (mode === "create" && changed.billType)
      form.setFieldsValue({
        oncePay: ["deposit", "other"].includes(changed.billType),
      });
    if (METER_KEYS.some((k) => k in changed)) {
      const [ws, we, es, ee] = METER_KEYS.map((k) => all[k]);
      if (
        [ws, we, es, ee].every((v) => typeof v === "number" && !Number.isNaN(v))
      ) {
        const amount = r2(
          (we - ws) * WATER_PRICE + (ee - es) * ELECTRICITY_PRICE,
        );
        form.setFieldsValue({ amount });
        syncStatus({ ...all, amount });
      } else {
        form.setFieldsValue({ amount: 0 });
      }
    }
    if ("amount" in changed) syncStatus(all);
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const type = mode === "create" ? values.billType : data.billType;
    const payload = {
      ...values,
      ...dateFields(values),
      billNames: type === "other" ? values.billNames : BILL_TYPE_MAP[type], // 非"其他"类型，名称即类型名
    };
    setSaving(true);
    try {
      if (mode === "edit") {
        const { host, billType: _t, paidAmount, ...rest } = payload; // 房间、类型、已付金额不随编辑提交
        await updateHostRentBill(id, rest);
        message.success("保存账单成功");
        billChange?.();
        close();
      } else {
        await createHostRentBill(payload);
        message.success("新建账单成功");
        billChange?.();
        close();
      }
    } catch (e) {
      message.error(
        e?.response?.data?.msg || e?.response?.data?.message || "保存账单失败",
      );
    } finally {
      setSaving(false);
    }
  };

  const editing = mode !== "view";
  const showDates = billType !== "deposit";
  const isWE = billType === "waterElectricity";
  const req = (m) => [{ required: true, message: m }];
  const dp = (ph, onPicked) => (
    <DatePicker
      placeholder={ph}
      format={FMT}
      inputReadOnly
      allowClear={true}
      suffixIcon={null}
      onPicked={onPicked}
      style={{ width: "100%" }}
    />
  );
  const meter = (name, ph) => (
    <Cell>
      <Item name={name}>
        <InputNumber
          controls={false}
          placeholder={ph}
          inputMode="decimal"
          style={{ width: "100%" }}
        />
      </Item>
    </Cell>
  );

  const d = data;
  return (
    <Drawer
      placement="top"
      open={open}
      onClose={requestClose}
      closable={false}
      height="auto"
      styles={{
        wrapper: { maxHeight: "92vh" },
        body: {
          padding: "calc(12px + env(safe-area-inset-top, 0px)) 12px 4px",
        },
        footer: {
          padding: "8px 12px calc(8px + env(safe-area-inset-bottom, 0px))",
        },
      }}
      footer={
        <div className="rbd-foot">
          {editing ? (
            <>
              <Button onClick={cancelEdit} disabled={saving}>
                {mode === "create" ? "取消" : "放弃修改"}
              </Button>
              <Button type="primary" onClick={handleSave} loading={saving}>
                保存
              </Button>
            </>
          ) : (
            <>
              <Button onClick={close}>关闭</Button>
              <Button type="primary" onClick={startEdit} disabled={!d}>
                编辑
              </Button>
            </>
          )}
        </div>
      }
    >
      <style>{css}</style>
      <div className="rbd" ref={rootRef} onKeyDown={onKeyDown}>
        <Spin spinning={loading}>
          <Form form={form} component="div" onValuesChange={onValuesChange}>
            {editing ? (
              <>
                <Row label="合约">
                  <Cell>
                    <Item name="contract" rules={req("请选择合约")}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        loading={optionsLoading}
                        options={contractOptions}
                        placeholder="选择合约"
                      />
                    </Item>
                  </Cell>
                </Row>
                <Row>
                  <Cell>
                    <Item name="host">
                      <HostSelect disabled placeholder="房间" />
                    </Item>
                  </Cell>
                  <Cell f="billType">
                    <Item name="billType" rules={req("请选择类型")}>
                      <Select
                        disabled={mode === "edit"}
                        options={BILL_TYPE_OPTIONS}
                        placeholder="账单类型"
                        onChange={() => goNext("billType")}
                      />
                    </Item>
                  </Cell>
                </Row>
                {isOther && (
                  <Row label="账单名称">
                    <Cell f="billNames">
                      <Item name="billNames">
                        <Input maxLength={30} />
                      </Item>
                    </Cell>
                  </Row>
                )}
                {showDates && (
                  <Row>
                    <Cell f="startDate">
                      <Item name="startDate">
                        {dp("开始日期", () => goNext("startDate"))}
                      </Item>
                    </Cell>
                    <Cell f="endDate">
                      <Item
                        name="endDate"
                        dependencies={["startDate"]}
                        rules={[
                          ...(isWE ? [] : req("结束日期")), // 水电账单的结束日期抄表时才有，可以留空
                          ({ getFieldValue }) => ({
                            validator: (_, v) => {
                              if (!v) return Promise.resolve();

                              const s = getFieldValue("startDate");

                              if (!s) {
                                return Promise.reject(
                                  new Error("请先选择开始日期"),
                                );
                              }

                              if (v.isBefore(s, "day")) {
                                return Promise.reject(
                                  new Error("不能早于开始日期"),
                                );
                              }

                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        {dp("结束日期", () => goNext("endDate"), isWE)}
                      </Item>
                    </Cell>
                  </Row>
                )}
                {isWE && (
                  <>
                    <Row>
                      {meter("waterMeterStart", "水表起")}
                      {meter("waterMeterEnd", "水表止")}
                    </Row>
                    <Row>
                      {meter("electricityMeterStart", "电表起")}
                      {meter("electricityMeterEnd", "电表止")}
                    </Row>
                  </>
                )}
                <Row>
                  <Cell flex={1.1} f="amount">
                    <Item name="amount" rules={isWE ? [] : req("应付金额")}>
                      <InputNumber
                        controls={false}
                        prefix="¥"
                        min={0}
                        precision={2}
                        placeholder="应付金额"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                    </Item>
                  </Cell>
                  <Cell flex={1.3} f="dueDate">
                    <Item name="dueDate" rules={req("账单日期")}>
                      {dp("账单日期", () => goNext("dueDate"))}
                    </Item>
                  </Cell>
                  <Item name="oncePay">
                    <ToggleChip>一次性</ToggleChip>
                  </Item>
                </Row>
                <Row>
                  <Cell>
                    <Item name="paidAmount">
                      <InputNumber
                        controls={false}
                        prefix="已付 ¥"
                        precision={2}
                        disabled
                        placeholder="已付金额"
                        style={{ width: "100%" }}
                      />
                    </Item>
                  </Cell>
                  <Cell>
                    <Item name="status" rules={req("账单状态")}>
                      <Select options={STATUS_OPTIONS} placeholder="账单状态" />
                    </Item>
                  </Cell>
                </Row>
                <Row label="备注">
                  <Cell>
                    <Item name="notes">
                      <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        maxLength={200}
                      />
                    </Item>
                  </Cell>
                </Row>
              </>
            ) : (
              d && (
                <>
                  <div className="rbd-head">
                    <span
                      className="rbd-type"
                      style={{
                        "--tc": BILL_TYPE_COLOR[d.billType] || "#64748b",
                      }}
                    >
                      {BILL_TYPE_MAP[d.billType]}
                    </span>
                    <span className="rbd-room">{d.roomNumber}</span>
                  </div>
                  <View label="合约">{d.contractText}</View>
                  {d.billType === "other" && (
                    <View label="名称">{d.billNames}</View>
                  )}
                  {d.billType !== "deposit" && (
                    <View label="周期">
                      {d.startDate?.format(FMT) || "-"} ～{" "}
                      {d.endDate?.format(FMT) || "-"}
                      {d.startDate && d.endDate
                        ? `（${d.endDate.diff(d.startDate, "day")}天）`
                        : ""}
                    </View>
                  )}
                  {d.billType === "waterElectricity" && (
                    <>
                      <View label="水表">
                        {d.waterMeterStart ?? "-"} → {d.waterMeterEnd ?? "-"}
                      </View>
                      <View label="电表">
                        {d.electricityMeterStart ?? "-"} →{" "}
                        {d.electricityMeterEnd ?? "-"}
                      </View>
                    </>
                  )}
                  <div className="rbd-v">
                    <span className="rbd-vl">应付</span>
                    <span className="rbd-vv" style={{ flex: "0 1 auto" }}>
                      ¥{n2(d.amount)}
                      {d.oncePay && <span className="rbd-tag">一次性</span>}
                    </span>
                    <span className="rbd-vr">
                      <span className="rbd-vl">账单日</span>
                      {d.dueDate?.format(FMT) || "-"}
                    </span>
                  </div>
                  <View label="已付">
                    ¥{n2(d.paidAmount ?? 0)}
                    <span
                      className="rbd-tag"
                      style={{ color: STATUS_META[d.status]?.color }}
                    >
                      {STATUS_META[d.status]?.label || d.status}
                    </span>
                  </View>
                  {d.notes ? <View label="备注">{d.notes}</View> : null}
                </>
              )
            )}
          </Form>
        </Spin>
      </div>
    </Drawer>
  );
}
